import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Stack} from '@mantine/core';
import React, {useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import type {CoreObjectId} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType, getObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionUtil';
import CollaborationManagementContent, {Policy} from 'collaborator-components/CollaborationManagementContent';
import PoliceableDAO from 'collaborator-components/PoliceableDAO';
import useCollaborationManagementState from 'collaborator-components/useCollaborationManagementState';
import {PolicyAction} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {RoutineRunAs} from 'llm/routines/RoutineTypes';
import notifications, {Response} from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';
import {isJqXHR} from 'util/Net';

import {RoutinePoliciesQuery, RoutinePoliciesQueryVariables} from './__generated/RoutinePoliciesQuery';

export const ROUTINE_POLICIES_QUERY = gql`
  query RoutinePoliciesQuery($routineId: StrictString!) @engComponent(name: LLM) @skipAudit {
    routine(id: $routineId) {
      id
      runAs
    }
    policyListing(resourceType: ROUTINE) {
      policies {
        __typename
        id
        name
        isHidden
        policyStatements {
          action
        }
      }
    }
  }
`;

interface RoutineAccessTabProps {
  routineId: string;
}

// NOTE: This component copies logic from CollaborationManagementModal. Consider a refactor for more re-use.
export default function RoutineAccessTab({routineId}: RoutineAccessTabProps): JSX.Element {
  const [collaborationState, collaborationActions] = useCollaborationManagementState(routineId);
  const policyResult = useBenchlingQuery<RoutinePoliciesQuery, RoutinePoliciesQueryVariables>(
    ROUTINE_POLICIES_QUERY,
    {variables: {routineId}}
  );
  const [saving, setSaving] = useState(false);
  const [canSave, setCanSave] = useState(true);

  const hasPendingChanges = useMemo(
    () => !isEqual(collaborationState.collaborations, collaborationState.initialCollaborations),
    [collaborationState.collaborations, collaborationState.initialCollaborations]
  );

  if (
    collaborationState.queryResultType === QueryResultType.ERROR ||
    policyResult.type === QueryResultType.ERROR
  ) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Could not load Agent access"
        supportingInfo="Try reloading the page."
      />
    );
  }
  if (
    collaborationState.queryResultType !== QueryResultType.SUCCESS ||
    policyResult.type !== QueryResultType.SUCCESS
  ) {
    return <CenteredSpinner />;
  }

  const canManageCollaborators = collaborationState.policeable?.permissions.canManageCollaborators ?? false;
  const readOnly = saving || !canManageCollaborators;
  const policies = policyResult.data.policyListing.policies satisfies Array<Policy>;
  const routineRunAs = policyResult.data.routine?.runAs;
  const allowsWriteCollaboratorPolicies = routineRunAs === RoutineRunAs.SERVICE_ACCOUNT;
  // Agents run in "Run-As-Creator" mode, which prevents collaborators from performing write actions.
  // Offering write policies would misleadingly suggest that those actions are available.
  const unavailablePolicyIds = allowsWriteCollaboratorPolicies
    ? []
    : policies
        .filter((policy) => policy.policyStatements.some(({action}) => action === PolicyAction.WRITE))
        .map(({id}) => id);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    const apiBase = getApiBaseForObjectType(getObjectType({id: routineId as CoreObjectId}));
    try {
      await PoliceableDAO.updateCollaborations(
        routineId,
        convertToCollaborationCreateOrUpdates(collaborationState.collaborations),
        apiBase
      );
      await collaborationActions.refetch?.();
      notifications.success('Successfully updated Agent access.');
    } catch (response) {
      if (!isJqXHR(response)) {
        throw response;
      }
      notifications.notifyError(response as Response);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing="md" {...getTestProps({component: 'RoutineAccessTab', element: 'root'})}>
      <CollaborationManagementContent
        {...collaborationState}
        {...collaborationActions}
        policies={policies}
        unavailablePolicyIds={unavailablePolicyIds}
        allowEditableOwner={false}
        isReadOnly={readOnly}
        renderAuditorColumn={false}
        setCanSave={setCanSave}
      />
      {canManageCollaborators && (
        <Group position="right">
          <Button
            loading={saving}
            disabled={!canSave || saving || !hasPendingChanges}
            onClick={handleSave}
            {...getTestProps({component: 'RoutineAccessTab', element: 'saveButton'})}
          >
            Save changes
          </Button>
        </Group>
      )}
    </Stack>
  );
}
